import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ButtonToolbar,
  Carousel,
  Spinner,
} from "react-bootstrap";
import { PersonCircle } from "react-bootstrap-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { common } from "../js/common";

interface RecipeImage {
  id: number;
  recipeId: number;
  url: string;
  sortOrder: number;
}

interface Recipe {
  id: number;
  name: string;
  description: string;
  creatorId: number;
  creatorName?: string;
  createdOn: string;
  previewImageUrl: string | null;
  galleryImages: RecipeImage[] | null;
}

interface SessionData {
  id: number;
  userName: string;
  role: string;
}

const THUMB = 120;

function RecipePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [session, setSession] = useState<SessionData | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [recipeRes, sessionRes] = await Promise.all([
          common.fetchRestAPI(`recipes/${id}`, "GET"),
          common.fetchRestAPI("sessions", "GET").catch(() => null),
        ]);

        if (!recipeRes.ok) {
          setError(recipeRes.status === 404 ? "Recipe not found" : "Failed to load recipe");
          setLoading(false);
          return;
        }

        const recipeData: Recipe = await recipeRes.json();
        setRecipe(recipeData);

        if (sessionRes && sessionRes.ok) {
          const sessionData: SessionData = await sessionRes.json();
          setSession(sessionData);
        }
      } catch {
        setError("Failed to load recipe");
      }
      setLoading(false);
    }
    fetchData();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this recipe?")) return;
    setDeleting(true);
    try {
      const res = await common.fetchRestAPI(`recipes/${id}`, "DELETE");
      if (res.ok) {
        navigate("/");
      } else if (res.status === 403) {
        alert("You don't have permission to delete this recipe.");
      } else {
        alert("Failed to delete recipe.");
      }
    } catch {
      alert("Failed to delete recipe.");
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center p-5">
        <Spinner animation="border" size="sm" />
      </Container>
    );
  }

  if (error || !recipe) {
    return (
      <Container className="p-5">
        <p className="alert alert-warning text-center">{error || "Recipe not found"}</p>
      </Container>
    );
  }

  const canModify =
    session && (session.id === recipe.creatorId || session.role === "admin");
  const gallery = [...(recipe.galleryImages ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Container className="p-4">
      <Row className="justify-content-center">
        <Col lg={9}>
          {canModify && (
            <Row className="justify-content-end mb-2">
              <Col className="col-auto">
                <ButtonToolbar>
                  <Button
                    variant="outline-danger"
                    className="mx-2"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </Button>
                  <Button variant="primary" href={`/submit?edit=${recipe.id}`}>
                    Edit
                  </Button>
                </ButtonToolbar>
              </Col>
            </Row>
          )}
          <Card className="p-4 bg-light">
            <Row className="align-items-center g-3 pb-3 border-bottom">
              <Col xs="auto">
                {recipe.previewImageUrl ? (
                  <img
                    src={recipe.previewImageUrl}
                    alt={`${recipe.name} preview`}
                    style={{ width: THUMB, height: THUMB, objectFit: "cover", borderRadius: 12 }}
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    style={{ width: THUMB, height: THUMB, borderRadius: 12, background: "#d9d9d9" }}
                  />
                )}
              </Col>
              <Col>
                <h2 className="fw-bold mb-0" style={{ overflowWrap: "anywhere" }}>
                  {recipe.name}
                </h2>
              </Col>
              <Col xs="auto" className="text-center">
                <div className="small text-start ps-1">By:</div>
                <PersonCircle size={64} aria-hidden="true" />
                <div className="mt-1" data-testid="recipe-author">
                  {recipe.creatorName || "Unknown"}
                </div>
              </Col>
            </Row>

            {gallery.length > 0 && (
              <div className="py-3 border-bottom">
                <Carousel
                  interval={null}
                  variant="dark"
                  controls={gallery.length > 1}
                  indicators={gallery.length > 1}
                  className="border rounded bg-white"
                >
                  {gallery.map((img, i) => (
                    <Carousel.Item key={img.id}>
                      <img
                        className="d-block w-100"
                        src={img.url}
                        alt={`${recipe.name}, ${i + 1} of ${gallery.length}`}
                        style={{ maxHeight: 520, objectFit: "contain" }}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            )}

            <div className="pt-3 recipe-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{recipe.description}</ReactMarkdown>
            </div>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default RecipePage;
